/**
 * Read-only date/time info bar for the booked slot.
 * Shows date + time range + edit icon.
 * Uses account date format preference via useDateFormat hook.
 */
import { useMemo } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { Edit02, Calendar, Clock } from '@kula-ai/icons-core';
import { useDateFormat } from '@/hooks/useDateFormat';
import moment from 'moment-timezone';

interface DateTimeFieldProps {
  /** Pipe-separated ISO: "startISO|endISO" */
  value?: string;
  /** Toggle into edit mode */
  onEdit?: () => void;
}

export const DateTimeField = ({ value, onEdit }: DateTimeFieldProps) => {
  const { dateFormat, timeFormat } = useDateFormat();

  const [startIso, endIso] = useMemo(() => {
    const val = typeof value === 'string' ? value : '';
    const parts = val.split('|');
    return [parts[0] || '', parts[1] || ''];
  }, [value]);

  if (!startIso && !endIso) return null;

  const formattedDate = startIso
    ? moment(startIso).format(dateFormat?.label || 'MM/DD/YYYY')
    : '';
  const formattedStart = startIso ? moment(startIso).format(timeFormat) : '';
  const formattedEnd = endIso ? moment(endIso).format(timeFormat) : '';
  const timeDisplay =
    formattedStart && formattedEnd
      ? `${formattedStart} - ${formattedEnd}`
      : formattedStart || formattedEnd;

  return (
    <Flex
      align="center"
      justify="space-between"
      w="100%"
      py="3"
      maxH="43px"
      px="17px"
      mt="-8px"
      bg="secondary-25"
      borderRadius="md"
    >
      <Flex align="center" gap="3" flexWrap="wrap">
        {formattedDate && (
          <Flex align="center" gap="1.5">
            <Calendar size={16} color="var(--kula-colors-secondary-700)" />
            <Text
              fontSize="14px"
              lineHeight="20px"
              color="secondary-900"
              style={{ fontWeight: 450 }}
            >
              {formattedDate}
            </Text>
          </Flex>
        )}
        {timeDisplay && (
          <Flex align="center" gap="1.5">
            <Clock size={16} color="var(--kula-colors-secondary-700)" />
            <Text
              fontSize="14px"
              lineHeight="20px"
              color="secondary-900"
              style={{ fontWeight: 450 }}
            >
              {timeDisplay}
            </Text>
          </Flex>
        )}
      </Flex>
      {onEdit && (
        <Flex
          as="button"
          type="button"
          align="center"
          justify="center"
          w="7"
          h="7"
          borderRadius="md"
          cursor="pointer"
          _hover={{ bg: 'secondary-100' }}
          onClick={onEdit}
          aria-label="Edit date and time"
        >
          <Edit02 size={16} color="var(--kula-colors-secondary-700)" />
        </Flex>
      )}
    </Flex>
  );
};
